import { Tag } from '@chakra-ui/react';
import { LocationPin } from '@/components/LocationPin';
import { AppHead } from './components/AppHead';
import Icon from '@mdi/react';
import {
    mdiAccountGroup,
    mdiCalendar,
    mdiClose,
    mdiEarth,
    mdiImageMultiple,
    mdiImport,
    mdiMap,
    mdiMapCheck,
    mdiMapMarker,
    mdiMenu,
    mdiNearMe,
    mdiNotebook,
    mdiTimeline,
} from '@mdi/js';
import { useState } from 'react';
import { SiteFooter } from '@/layouts/components/SiteFooter';
import { Button } from '@/trailmix/Button';
import { cn } from '@/trailmix/lib/cn';

function SquareIcon({
    path,
    variant,
    size,
}: {
    path: string;
    variant: 'blue' | 'green' | 'orange' | 'red';
    size: 'sm' | 'md';
}) {
    return (
        <div
            className={cn(
                'rounded-md border shadow-sm',
                variant === 'green' && 'border-green-300 bg-green-100 text-green-900',
                variant === 'orange' && 'border-orange-300 bg-orange-100 text-orange-900',
                variant === 'red' && 'border-red-300 bg-red-100 text-red-900',
                variant === 'blue' && 'border-sky-300 bg-sky-100 text-sky-900',
                size === 'sm' && 'p-1.5',
                size === 'md' && 'p-1.5 desktop:p-2'
            )}
        >
            <Icon path={path} className={cn(size === 'sm' && 'size-5', size === 'md' && 'size-7 desktop:size-8')} />
        </div>
    );
}

function TwoColumnLayoutContainer({ className, children }: { className?: string; children?: React.ReactNode }) {
    return (
        <section className={cn('px-6 py-16 desktop:py-24', className)}>
            <div className='mx-auto grid max-w-screen-lg grid-cols-1 place-items-center gap-16 desktop:grid-cols-2 desktop:gap-12 lg:gap-24'>
                {children}
            </div>
        </section>
    );
}

function TwoColumnImageContainer({ className, children }: { className?: string; children: React.ReactNode }) {
    return (
        <div className={cn('overflow-hidden rounded-lg border border-gray-300 bg-gray-100 shadow-lg', className)}>
            {children}
        </div>
    );
}

function TwoColumnContent({
    title,
    description,
    icon,
    className,
    children,
}: {
    title: string;
    description: string | React.ReactNode;
    icon: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
}) {
    return (
        <div className={cn(className)}>
            <div className='flex flex-col items-start gap-8 desktop:gap-8'>
                {icon}
                <h3 className='text-pretty text-4xl font-bold tracking-tight desktop:text-5xl'>{title}</h3>
            </div>
            <p className='mt-4 text-pretty text-xl font-medium text-slate-700 desktop:mt-6'>{description}</p>

            <div className='mt-12 flex flex-col gap-8'>{children}</div>
        </div>
    );
}

function BulletItem({ title, icon, children }: { title: string; icon: React.ReactNode; children?: React.ReactNode }) {
    return (
        <div className='flex items-start gap-4'>
            {icon}
            <div className='flex flex-col gap-1'>
                <h4 className='text-pretty text-xl font-semibold'>{title}</h4>
                <p className='text-pretty text-gray-700'>{children}</p>
            </div>
        </div>
    );
}

export default function Index() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <>
            <AppHead>
                <link
                    rel='preload'
                    as='image'
                    href='https://res.cloudinary.com/adventurelist/image/upload/w_1920,f_auto,q_auto/guillaume-galtier-3YrppYQPoCI-unsplash_k4cbhp.jpg'
                    fetchPriority='high'
                />
            </AppHead>
            <div className='relative shadow-lg'>
                <div className='absolute inset-0 -z-10'>
                    <div className='absolute inset-0 bg-[url("https://res.cloudinary.com/adventurelist/image/upload/f_auto/q_auto/guillaume-galtier-3YrppYQPoCI-unsplash_k4cbhp.jpg")] bg-cover bg-center' />
                </div>
                <div className='container mx-auto max-w-screen-xl px-4'>
                    <nav className='flex justify-between py-4 desktop:p-4'>
                        <div className='flex items-center'>
                            <div className='size-6'>
                                <LocationPin />
                            </div>
                            <h1 className='ml-1.5 text-lg font-semibold text-gray-800'>AdventureList</h1>
                            <div className='ml-4'>
                                <Tag colorScheme='blue' fontWeight='semibold'>
                                    Early Access
                                </Tag>
                            </div>
                        </div>

                        {/* Desktop nav */}
                        <div className='hidden gap-2 desktop:flex'>
                            <Button as='a' href={route('login')} size='sm'>
                                Log in
                            </Button>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            className='flex items-center justify-center rounded-md bg-opacity-100 p-2 text-gray-700 desktop:hidden'
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            <Icon path={mobileMenuOpen ? mdiClose : mdiMenu} className='size-6' />
                        </button>
                    </nav>

                    <div className='relative flex flex-col items-center'>
                        {/* Mobile menu dropdown */}
                        {mobileMenuOpen && (
                            <div className='absolute inset-x-0 top-0 z-10 -mx-4 border-y border-gray-400 bg-white/30 backdrop-blur-md desktop:hidden'>
                                <div className='flex flex-col'>
                                    <a
                                        href={route('login')}
                                        className='px-4 py-3 text-base font-medium text-gray-800 hover:bg-gray-50'
                                    >
                                        Log in
                                    </a>
                                    <a
                                        href={route('register')}
                                        className='px-4 py-3 text-base font-medium text-gray-800 hover:bg-gray-50'
                                    >
                                        Sign up
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className='flex max-w-5xl flex-col px-2 pt-20 desktop:items-center desktop:pt-32 desktop:text-center'>
                            <h2 className='text-pretty text-4xl font-bold tracking-tight text-slate-900 desktop:text-7xl'>
                                The social travel journal for your must-visit places
                            </h2>
                            <p className='mt-6 text-pretty text-xl font-medium tracking-tight text-slate-700 desktop:mt-8 desktop:max-w-2xl desktop:text-2xl desktop:leading-normal'>
                                You've got place recs saved everywhere: Instagram/TikTok saves, screenshots, your Notes
                                app. AdventureList puts them all on one beautiful map so you can actually get out there.
                            </p>
                            <div className='mt-8 desktop:mt-12'>
                                <Button as='a' href={route('register')} variant='primary'>
                                    Start exploring (it&apos;s free!)
                                </Button>
                            </div>
                        </div>
                        <div className='py-20 desktop:mt-12 desktop:py-16'>
                            <div className='w-fit overflow-hidden rounded-lg border border-gray-300 bg-gray-100 px-3 py-4 shadow-lg desktop:px-4 desktop:py-5'>
                                <img
                                    src='https://res.cloudinary.com/adventurelist/image/upload/w_1200,f_auto,q_auto/app6-min_qqcfbi'
                                    alt='App screenshot'
                                    className='desktop:max-w-xl'
                                    width={576}
                                    height={841}
                                    fetchPriority='high'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TwoColumnLayoutContainer className='bg-gray-50'>
                <TwoColumnContent
                    icon={<SquareIcon path={mdiMapMarker} size='md' variant='blue' />}
                    title='Remember where you want to go'
                    description={
                        <>
                            Add all your must-visit places to <span className='font-semibold'>Planning</span> so you
                            know exactly where to go next.
                        </>
                    }
                >
                    <BulletItem
                        title='Import from anywhere'
                        icon={<SquareIcon path={mdiImport} size='sm' variant='blue' />}
                    >
                        Search for places to add or import your saved places from Google Maps, Instagram, or TikTok.
                    </BulletItem>
                    <BulletItem
                        title="See what's nearby"
                        icon={<SquareIcon path={mdiNearMe} size='sm' variant='blue' />}
                    >
                        Never miss a spot while you&apos;re on the go. Use{' '}
                        <span className='font-semibold'>Near me</span> to filter the map by what&apos;s close to you.
                    </BulletItem>
                </TwoColumnContent>
                <TwoColumnImageContainer className='p-2'>
                    <img
                        src='https://res.cloudinary.com/adventurelist/image/upload/w_800,f_auto,q_auto/planning-nearby_nnesvy.png'
                        alt='Screenshot of two lists, first is Paris Trip 2024 and second is Chicago Coffee Shops'
                        width={800}
                        height={792}
                        loading='lazy'
                        decoding='async'
                    />
                </TwoColumnImageContainer>
            </TwoColumnLayoutContainer>

            <TwoColumnLayoutContainer className='bg-gray-50'>
                <TwoColumnContent
                    icon={<SquareIcon path={mdiNotebook} size='md' variant='orange' />}
                    title='Stay organized'
                    description="Your friend's restaurant rec shouldn't get lost in your saved places. Create lists to keep things organized."
                >
                    <BulletItem
                        title='Map out your next trip'
                        icon={<SquareIcon path={mdiCalendar} size='sm' variant='orange' />}
                    >
                        Collect cool spots you want to visit on vacation, then log all your visits to remember how your
                        trip went.
                    </BulletItem>
                    <BulletItem
                        title='Make themed lists'
                        icon={<SquareIcon path={mdiMapMarker} size='sm' variant='orange' />}
                    >
                        Build lists for goals that inspire you, like visiting all national parks or finding the best
                        restaurants in your city.
                    </BulletItem>
                </TwoColumnContent>

                <TwoColumnImageContainer className='p-1 desktop:order-first'>
                    <img
                        src='https://res.cloudinary.com/adventurelist/image/upload/w_800,f_auto,q_auto/lists-min_nnbwx9'
                        alt='Screenshot of two lists, first is Paris Trip 2024 and second is Chicago Coffee Shops'
                        width={800}
                        height={373}
                        loading='lazy'
                        decoding='async'
                    />
                </TwoColumnImageContainer>
            </TwoColumnLayoutContainer>

            <TwoColumnLayoutContainer className='bg-green-50'>
                <TwoColumnContent
                    icon={<SquareIcon path={mdiMapCheck} size='md' variant='green' />}
                    title="Log what you've experienced"
                    description='Capture what made each place special by saving photos and notes from your visits.'
                >
                    <BulletItem
                        title='Go back in time'
                        icon={<SquareIcon path={mdiTimeline} size='sm' variant='green' />}
                    >
                        Look back on all of your experiences in your <span className='font-semibold'>Logbook</span>, a
                        chronological timeline of all the places you&apos;ve visited.
                    </BulletItem>
                </TwoColumnContent>
                <TwoColumnImageContainer className='p-1'>
                    <img
                        src='https://res.cloudinary.com/adventurelist/image/upload/w_800,f_auto,q_auto/edit_modal-min_sgaiwu'
                        alt='Screenshot of edit modal'
                        className='rounded-md'
                        width={800}
                        height={850}
                        loading='lazy'
                        decoding='async'
                    />
                </TwoColumnImageContainer>
            </TwoColumnLayoutContainer>

            <TwoColumnLayoutContainer className='bg-red-50'>
                <TwoColumnContent
                    icon={<SquareIcon path={mdiEarth} size='md' variant='red' />}
                    title='Stay connected'
                    description='Your profile brings together your saved places, photos, and stats in one place.'
                >
                    <BulletItem title='Dig into your map' icon={<SquareIcon path={mdiMap} size='sm' variant='red' />}>
                        All your saved places are displayed on an interactive map on your profile so you can easily
                        visualize where you&apos;ve been and decide where you want to go next.
                    </BulletItem>
                    <BulletItem
                        title='Look back on your photos'
                        icon={<SquareIcon path={mdiImageMultiple} size='sm' variant='red' />}
                    >
                        Look back on your travel memories with a gallery of photos from all the places you&apos;ve
                        visited.
                    </BulletItem>
                    <BulletItem
                        title='Keep up with friends'
                        icon={<SquareIcon path={mdiAccountGroup} size='sm' variant='red' />}
                    >
                        Follow your friends to see their latest updates and recommendations.
                    </BulletItem>
                </TwoColumnContent>

                <TwoColumnImageContainer className='p-2 desktop:order-first'>
                    <img
                        src='https://res.cloudinary.com/adventurelist/image/upload/w_800,f_auto,q_auto/profile2-min_zuch00'
                        alt='Screenshot of user profile'
                        className='rounded-md'
                        width={800}
                        height={825}
                        loading='lazy'
                        decoding='async'
                    />
                </TwoColumnImageContainer>
            </TwoColumnLayoutContainer>

            <div className='bg-[url("https://res.cloudinary.com/adventurelist/image/upload/f_auto/q_auto/guillaume-galtier-3YrppYQPoCI-unsplash_k4cbhp.jpg")] bg-[2000px_10%] shadow-md desktop:bg-cover desktop:bg-[0_0]'>
                <div className='container relative mx-auto max-w-screen-lg px-8 py-32 text-center lg:py-52'>
                    <h2 className='text-pretty text-4xl font-bold desktop:text-6xl'>
                        The places you've saved are waiting
                    </h2>
                    <div className='mt-12'>
                        <Button as='a' href={route('register')} variant='primary' size='lg'>
                            Get started for free
                        </Button>
                    </div>
                </div>
            </div>
            <SiteFooter />
        </>
    );
}
